import { createContext, useContext, useState, useEffect } from "react";

const translations = {
  sv: {
    // Navbar
    nav: {
      services: "Tjänster",
      fleet: "Bilflotta",
      about: "Om Oss",
      contact: "Kontakt",
      bookNow: "Boka Nu",
    },
    // Hero
    hero: {
      badge: "Kalmar & Öland",
      title: "Din Pålitliga",
      titleHighlight: "Taxipartner",
      description: "Vi erbjuder säkra resor i tid med hög kvalité för företag, privatpersoner och pensionärer. Boka enkelt via vår app eller ring oss direkt.",
      safeRides: "Trygga resor",
      allDay: "Dygnet runt",
      fiveStar: "5-stjärnig service",
      bookRide: "Boka Din Resa",
      callUs: "Ring",
      downloadApp: "Ladda ner vår app för priser",
    },
    // About Section
    about: {
      title: "Om Oss",
      subtitle: "Taxi Kalmar AB",
      story: "Taxi Kalmar AB grundades i november 2020 med en vision att erbjuda pålitliga och trygga taxitjänster i Kalmar och på Öland. Vi startade med en enda bil och en dröm om att leverera förstklassig service.",
      growth: "Idag har vi vuxit till fem moderna bilar och åtta dedikerade anställda. Vår resa har bara börjat – vi fortsätter att utvecklas och expandera för att möta våra kunders behov.",
      mission: "Vår mission är enkel: att ge varje kund en trygg, bekväm och punktlig resa. Oavsett om du är företagskund, privatperson eller pensionär – vi tar hand om dig.",
      stats: {
        cars: "5 bilar",
        employees: "8 anställda", 
        since: "Sedan 2020",
        available: "Dygnet runt"
      }
    },
    // Services
    services: {
      title: "Våra Tjänster",
      subtitle: "Vad kan vi hjälpa dig med?",
      description: "Vi erbjuder ett komplett utbud av transporttjänster för företag, privatpersoner och pensionärer i Kalmar och på Öland. Vi finns här för dig dygnet runt.",
      airport: {
        title: "Flygtaxi",
        description: "Professionell transport till och från Kalmar Airport för företag och privatpersoner",
        features: [
          "Punktlig upphämtning – vi övervakar flygtider",
          "Hämtar dig vid ankomst och ser till att du kommer fram tryggt och säkert",
          "Tidig morgon och sen kväll – vi kör dygnet runt",
          "Väntar utan extra kostnad vid försenat flyg",
          "Hjälp med bagaget hela vägen"
        ],
        highlight: "Vi ser till att du aldrig missar ditt flyg!"
      },
      hotel: {
        title: "Hotellsamarbete",
        description: "Vi samarbetar med hotell i Kalmar och på Öland för att erbjuda våra gäster smidig transport",
        features: [
          "Samarbete med lokala hotell i hela regionen",
          "Transport till konferenser och evenemang",
          "Turistturer på Öland och i Kalmar med kunnig förare",
          "Sightseeing och rundturer för hotelgäster",
          "Gruppbokningar för turistgrupper och företag"
        ],
        highlight: "Perfekt för turister och affärsresenärer!"
      },
      courier: {
        title: "Budtransport",
        description: "Snabb och säker leverans av paket och dokument – vi har kört från Kalmar till Arvika och Stockholm",
        features: [
          "Långdistans budkörningar (Kalmar-Stockholm, Kalmar-Arvika m.fl.)",
          "Expressleverans samma dag i hela regionen",
          "Säker hantering av känsliga dokument och paket",
          "Apoteksärenden och inköp för dig som behöver hjälp",
          "Flexibla tider – även kvällar och helger"
        ],
        highlight: "Tryggt, snabbt och pålitligt – oavsett avstånd!"
      },
      personal: {
        title: "Persontransport",
        description: "Taxiresor för alla – från korta turer till längre resor med fokus på trygghet",
        features: [
          "Sjukresor och läkarbesök – samarbete med försäkringsbolag",
          "Olycksfallsförsäkring för trygg transport",
          "Kvällsresor och nattkörning dygnet runt",
          "Bröllop och speciella tillfällen",
          "Alla priser finns i vår app"
        ],
        highlight: "Din personliga chaufför i Kalmar!"
      },
      school: {
        title: "Skoltransport",
        description: "Säker och pålitlig transport för barn och ungdomar till och från skolan",
        features: [
          "Erfarna förare med fokus på barnsäkerhet",
          "Punktlig hämtning och lämning",
          "Barnstolar och säkerhetsutrustning",
          "Kontakt med föräldrar vid behov",
          "Flexibla lösningar för olika behov"
        ],
        highlight: "Trygga resor för era barn!"
      },
      elderly: {
        title: "Pensionärstjänster",
        description: "Extra omsorg för våra äldre kunder – vi hjälper dig hela vägen",
        features: [
          "Dörr-till-dörr service – vi hämtar dig vid din dörr",
          "Hjälp att ta dig i och ur bilen",
          "Vi bär dina saker och lämnar dem vid din dörr",
          "Extra tid och tålamod för dig som behöver",
          "Personlig service med omtanke"
        ],
        highlight: "Vi tar hand om dig som du förtjänar!"
      },
      business: {
        title: "Företagstjänster",
        description: "Skräddarsydda transportlösningar för ditt företag",
        features: [
          "Hämta och lämna medarbetare och kunder",
          "Företagsavtal med fakturabetalning",
          "Transport till möten och konferenser",
          "VIP-transport för era viktigaste kunder",
          "Flexibla lösningar anpassade efter ert behov"
        ],
        highlight: "Låt oss ta hand om era transporter!"
      },
      specialService: "Behöver du en specialtjänst?",
      specialServiceDesc: "Vi erbjuder skräddarsydda transportlösningar för alla behov. Ladda ner vår app för att se priser och boka enkelt!",
      callUsBtn: "Ring Oss",
      sendRequest: "Skicka Förfrågan",
    },
    // Fleet
    fleet: {
      title: "Vår Bilflotta",
      subtitle: "Våra Taxibilar",
      description: "Vi kör moderna och fräscha bilar för att ge dig den bästa reseupplevelsen. Från 4 till 8 passagerare.",
      seatsFor: "Plats för",
      passengers: "passagerare",
    },
    // Benefits
    benefits: {
      title: "Varför Välja Oss",
      subtitle: "Varför ska du välja oss?",
      description: "Vi strävar efter att ge dig den bästa taxiupplevelsen i Kalmar och på Öland.",
      safe: {
        title: "Tryggt & Säkert",
        description: "Vi erbjuder pålitliga tjänster med moderna bilar. Våra förare har rätt kunskap inom trafiksäkerhet. Samarbete med försäkringsbolag för olycksfallsförsäkring."
      },
      customerService: {
        title: "Utmärkt Kundservice",
        description: "Vi gör det till högsta prioritet att få en taxi till dig snabbt och smidigt. Extra omsorg för pensionärer med dörr-till-dörr service."
      },
      easyBooking: {
        title: "Lätt att Boka",
        description: "Ladda ner vår app för att enkelt boka taxi och se våra priser. Du kan också ringa oss direkt dygnet runt."
      },
      satisfaction: {
        title: "Nöjd Kund Garanti",
        description: "Vi garanterar att våra taxibilar kommer i tid och transporterar dig på ett säkert och effektivt sätt."
      }
    },
    // App Section
    app: {
      badge: "Vår App",
      title: "Boka direkt i appen!",
      description: "Ladda ner vår app för att enkelt boka taxi, se våra priser, följa din resa i realtid och få tillgång till exklusiva erbjudanden.",
      downloadOn: "Ladda ner på",
      appStore: "App Store",
      getItOn: "Hämta den på",
      googlePlay: "Google Play",
      bookYourRide: "Boka din resa",
      download: "Ladda ner",
      seePrices: "Se priser i appen",
    },
    // Contact
    contact: {
      title: "Kontakta Oss",
      subtitle: "Hör av dig till oss",
      description: "Har du frågor eller vill boka? Kontakta oss via formuläret eller ring direkt. Vi finns här dygnet runt!",
      sendMessage: "Skicka ett meddelande",
      yourName: "Ditt namn",
      yourEmail: "Din e-post",
      yourPhone: "Ditt telefonnummer",
      yourMessage: "Ditt meddelande",
      send: "Skicka Meddelande",
      sending: "Skickar...",
      address: "Adress",
      phone: "Telefon",
      email: "E-post",
      openHours: "Öppettider",
      openAllDay: "Dygnet runt, 7 dagar i veckan",
      messageSent: "Meddelande skickat!",
      messageConfirm: "Vi återkommer till dig så snart som möjligt.",
      messageError: "Något gick fel",
      messageErrorDesc: "Försök igen eller ring oss direkt.",
    },
    // Booking Modal
    booking: {
      title: "Boka Din Taxi",
      description: "Fyll i formuläret så bekräftar vi din bokning inom kort. Se priser i vår app.",
      yourName: "Ditt namn *",
      phone: "Telefonnummer *",
      email: "E-post (valfritt)",
      pickup: "Hämtplats *",
      destination: "Destination *",
      message: "Meddelande (valfritt) - t.ex. flygnummer, speciella önskemål, behov av hjälp",
      passengers: "Passagerare",
      submit: "Skicka Bokningsförfrågan",
      submitting: "Skickar...",
      cancel: "Avbryt",
      orCall: "Eller ring oss direkt:",
      bookingReceived: "Bokning mottagen!",
      bookingConfirm: "Vi bekräftar din bokning via telefon eller e-post.",
      bookingError: "Något gick fel",
      bookingErrorDesc: "Försök igen eller ring oss direkt på 0480 47 47 17.",
    },
    // Footer
    footer: {
      description: "Din pålitliga taxipartner i Kalmar och på Öland sedan 2020. Trygga resor för företag, privatpersoner och pensionärer dygnet runt.",
      quickLinks: "Snabblänkar",
      services: "Tjänster",
      contact: "Kontakt",
      allRights: "Alla rättigheter förbehållna.",
      privacyPolicy: "Integritetspolicy",
      servicesList: [
        "Flygtaxi",
        "Hotellsamarbete",
        "Budtransport",
        "Skoltransport",
        "Pensionärstjänster",
        "Företagstransport"
      ]
    }
  },
  en: {
    // Navbar
    nav: {
      services: "Services",
      fleet: "Our Fleet",
      about: "About Us",
      contact: "Contact",
      bookNow: "Book Now",
    },
    // Hero
    hero: {
      badge: "Kalmar & Öland",
      title: "Your Reliable",
      titleHighlight: "Taxi Partner",
      description: "We offer safe and punctual rides with high quality for businesses, private customers and seniors. Book easily via our app or call us directly.",
      safeRides: "Safe rides",
      allDay: "24/7 service",
      fiveStar: "5-star service",
      bookRide: "Book Your Ride",
      callUs: "Call",
      downloadApp: "Download our app for prices",
    },
    // About Section
    about: {
      title: "About Us",
      subtitle: "Taxi Kalmar AB",
      story: "Taxi Kalmar AB was founded in November 2020 with a vision to provide reliable and safe taxi services in Kalmar and Öland. We started with just one car and a dream to deliver first-class service.",
      growth: "Today we have grown to five modern vehicles and eight dedicated employees. Our journey has just begun – we continue to develop and expand to meet our customers' needs.",
      mission: "Our mission is simple: to give every customer a safe, comfortable and punctual ride. Whether you are a business customer, private individual or senior – we take care of you.",
      stats: {
        cars: "5 cars",
        employees: "8 employees",
        since: "Since 2020",
        available: "24/7"
      }
    },
    // Services
    services: {
      title: "Our Services",
      subtitle: "How can we help you?",
      description: "We offer a complete range of transport services for businesses, private customers and seniors in Kalmar and Öland. We are here for you 24/7.",
      airport: {
        title: "Airport Taxi",
        description: "Professional transport to and from Kalmar Airport for businesses and private customers",
        features: [
          "Punctual pickup – we monitor flight times",
          "We pick you up on arrival and make sure you get there safely",
          "Early morning and late night – we drive 24/7",
          "Free waiting for delayed flights",
          "Help with luggage all the way"
        ],
        highlight: "We make sure you never miss your flight!"
      },
      hotel: {
        title: "Hotel Partnership",
        description: "We partner with hotels in Kalmar and Öland to offer smooth transport for guests",
        features: [
          "Partnerships with local hotels throughout the region",
          "Transport to conferences and events",
          "Tourist tours on Öland and in Kalmar with knowledgeable driver",
          "Sightseeing and tours for hotel guests",
          "Group bookings for tourist groups and businesses"
        ],
        highlight: "Perfect for tourists and business travelers!"
      },
      courier: {
        title: "Courier Service",
        description: "Fast and secure delivery of packages and documents – we have driven from Kalmar to Arvika and Stockholm",
        features: [
          "Long-distance courier services (Kalmar-Stockholm, Kalmar-Arvika etc.)",
          "Same-day express delivery throughout the region",
          "Secure handling of sensitive documents and packages",
          "Pharmacy errands and shopping for those who need help",
          "Flexible hours – evenings and weekends too"
        ],
        highlight: "Safe, fast and reliable – regardless of distance!"
      },
      personal: {
        title: "Personal Transport",
        description: "Taxi rides for everyone – from short trips to longer journeys with focus on safety",
        features: [
          "Medical trips and doctor visits – partnership with insurance companies",
          "Accident insurance for safe transport",
          "Evening and night driving 24/7",
          "Weddings and special occasions",
          "All prices available in our app"
        ],
        highlight: "Your personal chauffeur in Kalmar!"
      },
      school: {
        title: "School Transport",
        description: "Safe and reliable transport for children to and from school",
        features: [
          "Experienced drivers focused on child safety",
          "Punctual pickup and drop-off",
          "Child seats and safety equipment",
          "Contact with parents when needed",
          "Flexible solutions for different needs"
        ],
        highlight: "Safe journeys for your children!"
      },
      elderly: {
        title: "Senior Services",
        description: "Extra care for our elderly customers – we help you all the way",
        features: [
          "Door-to-door service – we pick you up at your door",
          "Help getting in and out of the car",
          "We carry your belongings and leave them at your door",
          "Extra time and patience for those who need it",
          "Personal service with care"
        ],
        highlight: "We take care of you as you deserve!"
      },
      business: {
        title: "Business Services",
        description: "Tailored transport solutions for your company",
        features: [
          "Pick up and drop off employees and customers",
          "Business contracts with invoice payment",
          "Transport to meetings and conferences",
          "VIP transport for your most important customers",
          "Flexible solutions adapted to your needs"
        ],
        highlight: "Let us take care of your transport!"
      },
      specialService: "Need a special service?",
      specialServiceDesc: "We offer customized transport solutions for all needs. Download our app to see prices and book easily!",
      callUsBtn: "Call Us",
      sendRequest: "Send Request",
    },
    // Fleet
    fleet: {
      title: "Our Fleet",
      subtitle: "Our Taxi Cars",
      description: "We drive modern and fresh cars to give you the best travel experience. From 4 to 8 passengers.",
      seatsFor: "Seats for",
      passengers: "passengers",
    },
    // Benefits
    benefits: {
      title: "Why Choose Us",
      subtitle: "Why should you choose us?",
      description: "We strive to give you the best taxi experience in Kalmar and Öland.",
      safe: {
        title: "Safe & Secure",
        description: "We offer reliable services with modern cars. Our drivers have the right knowledge in traffic safety. Partnership with insurance companies for accident insurance."
      },
      customerService: {
        title: "Excellent Customer Service",
        description: "We make it a top priority to get a taxi to you quickly and smoothly. Extra care for seniors with door-to-door service."
      },
      easyBooking: {
        title: "Easy to Book",
        description: "Download our app to easily book a taxi and see our prices. You can also call us directly 24/7."
      },
      satisfaction: {
        title: "Satisfaction Guarantee",
        description: "We guarantee that our taxis arrive on time and transport you safely and efficiently."
      }
    },
    // App Section
    app: {
      badge: "Our App",
      title: "Book directly in the app!",
      description: "Download our app to easily book a taxi, see our prices, track your ride in real-time and get access to exclusive offers.",
      downloadOn: "Download on",
      appStore: "App Store",
      getItOn: "Get it on",
      googlePlay: "Google Play",
      bookYourRide: "Book your ride",
      download: "Download",
      seePrices: "See prices in the app",
    },
    // Contact
    contact: {
      title: "Contact Us",
      subtitle: "Get in touch with us",
      description: "Have questions or want to book? Contact us via the form or call directly. We are here 24/7!",
      sendMessage: "Send a message",
      yourName: "Your name",
      yourEmail: "Your email",
      yourPhone: "Your phone number",
      yourMessage: "Your message",
      send: "Send Message",
      sending: "Sending...",
      address: "Address",
      phone: "Phone",
      email: "Email",
      openHours: "Opening hours",
      openAllDay: "24/7, 7 days a week",
      messageSent: "Message sent!",
      messageConfirm: "We will get back to you as soon as possible.",
      messageError: "Something went wrong",
      messageErrorDesc: "Try again or call us directly.",
    },
    // Booking Modal
    booking: {
      title: "Book Your Taxi",
      description: "Fill in the form and we will confirm your booking shortly. See prices in our app.",
      yourName: "Your name *",
      phone: "Phone number *",
      email: "Email (optional)",
      pickup: "Pickup location *",
      destination: "Destination *",
      message: "Message (optional) - e.g. flight number, special requests, need for assistance",
      passengers: "Passengers",
      submit: "Send Booking Request",
      submitting: "Sending...",
      cancel: "Cancel",
      orCall: "Or call us directly:",
      bookingReceived: "Booking received!",
      bookingConfirm: "We will confirm your booking by phone or email.",
      bookingError: "Something went wrong",
      bookingErrorDesc: "Try again or call us directly at 0480 47 47 17.",
    },
    // Footer
    footer: {
      description: "Your reliable taxi partner in Kalmar and Öland since 2020. Safe rides for businesses, private customers and seniors 24/7.",
      quickLinks: "Quick Links",
      services: "Services",
      contact: "Contact",
      allRights: "All rights reserved.",
      privacyPolicy: "Privacy Policy",
      servicesList: [
        "Airport Taxi",
        "Hotel Partnership",
        "Courier Service",
        "School Transport",
        "Senior Services",
        "Business Transport"
      ]
    }
  }
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('taxi-nora-lang') || 'sv';
    }
    return 'sv';
  });

  useEffect(() => {
    localStorage.setItem('taxi-nora-lang', language);
  }, [language]);

  const t = translations[language];

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'sv' ? 'en' : 'sv');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};
